import React from "react";

import { Pencil, Plus, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import SectionCard from "@/pages/appointmentDetails/SectionCard";

export default function AppointmentProspectDetailsSection({
  segmentedToggleWrapper,
  segmentedToggleBase,
  segmentedToggleActive,
  hasProspectToggle,
  activeProspectIndex,
  primaryProspect,
  secondaryProspect,
  setActiveProspectId,

  accentIconButtonClass,
  saveIconButtonClass,
  setAddProspectOpen,

  isProspectEditing,
  setIsProspectEditing,
  isProspectSaving,
  handleSaveProspectDetails,

  prospects,

  activeProspectContact,
  displayLead,
  updateProspectContact,
  prospectDob,

  appt,
  fmtDateTime,
}) {
  const prospectStatus = (() => {
    const customerStatus = appt?.summary_payload?.customer_status;
    if (customerStatus === "Sold") return "Sold";
    if (customerStatus === "Dead Deal") return "Dead";
    return "Open";
  })();
  return (
    <SectionCard
      title="Prospect Details"
      right={
        <div className="flex items-center justify-end gap-2">
          {hasProspectToggle ? (
            <div className={segmentedToggleWrapper} data-testid="appointment-prospect-toggle">
              <button
                type="button"
                className={`${segmentedToggleBase} ${activeProspectIndex === 0 ? segmentedToggleActive : ""}`}
                onClick={() => primaryProspect?.id && setActiveProspectId(primaryProspect.id)}
                data-testid="appointment-prospect-toggle-primary"
              >
                {primaryProspect?.name || "Prospect 1"}
              </button>
              <button
                type="button"
                className={`${segmentedToggleBase} ${activeProspectIndex === 1 ? segmentedToggleActive : ""}`}
                onClick={() => secondaryProspect?.id && setActiveProspectId(secondaryProspect.id)}
                data-testid="appointment-prospect-toggle-secondary"
              >
                {secondaryProspect?.name || "Prospect 2"}
              </button>
            </div>
          ) : null}

          <Button
            type="button"
            variant="secondary"
            className={accentIconButtonClass}
            onClick={() => setAddProspectOpen(true)}
            data-testid="appointment-add-prospect-button"
          >
            <Plus className="h-4 w-4" />
          </Button>

          {isProspectEditing ? (
            <Button
              type="button"
              variant="secondary"
              className={saveIconButtonClass}
              onClick={handleSaveProspectDetails}
              disabled={isProspectSaving}
              data-testid="appointment-prospect-save-button"
            >
              <Save className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              className={accentIconButtonClass}
              onClick={() => setIsProspectEditing(true)}
              data-testid="appointment-prospect-edit-button"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {prospects.length ? null : (
          <div className="text-sm text-slate-200/70" data-testid="appointment-no-prospects">
            No prospects found for this appointment yet.
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Name</div>
              <div className="text-slate-100" data-testid="appointment-prospect-name">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.name || displayLead?.full_name || ""}
                    onChange={(event) => updateProspectContact("name", event.target.value)}
                    placeholder="Enter name"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-name-input"
                  />
                ) : (
                  activeProspectContact.name || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Date of Birth</div>
              <div className="text-slate-100" data-testid="appointment-prospect-dob">
                {isProspectEditing ? (
                  <Input
                    type="date"
                    value={activeProspectContact.dob || ""}
                    onChange={(event) => updateProspectContact("dob", event.target.value)}
                    placeholder="Enter date of birth"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-dob-input"
                  />
                ) : (
                  prospectDob
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Phone</div>
              <div className="text-slate-100" data-testid="appointment-prospect-phone">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.phone}
                    onChange={(event) => updateProspectContact("phone", event.target.value)}
                    placeholder="Enter phone"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-phone-input"
                  />
                ) : (
                  activeProspectContact.phone || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Email ID</div>
              <div className="text-slate-100" data-testid="appointment-prospect-email">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.email}
                    onChange={(event) => updateProspectContact("email", event.target.value)}
                    placeholder="Enter email"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-email-input"
                  />
                ) : (
                  activeProspectContact.email || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Address</div>
              <div className="text-slate-100" data-testid="appointment-prospect-address">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.address || ""}
                    onChange={(event) => updateProspectContact("address", event.target.value)}
                    placeholder="Enter address"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-address-input"
                  />
                ) : (
                  activeProspectContact.address || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">City</div>
              <div className="text-slate-100" data-testid="appointment-prospect-city">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.city || ""}
                    onChange={(event) => updateProspectContact("city", event.target.value)}
                    placeholder="Enter city"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-city-input"
                  />
                ) : (
                  activeProspectContact.city || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">State</div>
              <div className="text-slate-100" data-testid="appointment-prospect-state">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.state || ""}
                    onChange={(event) => updateProspectContact("state", event.target.value)}
                    placeholder="Enter state"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-state-input"
                  />
                ) : (
                  activeProspectContact.state || "—"
                )}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Zip</div>
              <div className="text-slate-100" data-testid="appointment-prospect-zip">
                {isProspectEditing ? (
                  <Input
                    value={activeProspectContact.zip || ""}
                    onChange={(event) => updateProspectContact("zip", event.target.value)}
                    placeholder="Enter zip"
                    className="h-9 bg-white/5 text-slate-100"
                    data-testid="appointment-prospect-zip-input"
                  />
                ) : (
                  activeProspectContact.zip || "—"
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Referral Agent</div>
              <div className="text-slate-100" data-testid="appointment-referral-agent">
                {appt.referral_agent_name || appt.referral_agent || "—"}
              </div>
            </div>

            {/* P4-DEBT-B: DISPLAY ONLY. This pane used to offer a Sales Agent
                selector in prospect-edit mode, writing the choice into the
                parent's `appt` state and submitting it through the generic
                `PUT /appointments/{id}`. `AppointmentUpdate` carries no actor
                field and runs `extra="ignore"` (WP-14B), so the request
                returned 200 with the actors dropped -- the save appeared to
                succeed, the chosen name was rendered from local state, and the
                server value returned on the next read. Actor reassignment is
                WP-14C's `POST /appointments/{id}/reassign` and nothing else. */}
            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Sales Agent</div>
              <div className="text-slate-100" data-testid="appointment-sales-agent">
                {appt.sales_agent_name || appt.sales_agent || "—"}
              </div>
            </div>

            {/* P4-DEBT-B: DISPLAY ONLY -- see the Sales Agent note above. */}
            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Booking Agent</div>
              <div className="text-slate-100" data-testid="appointment-booking-agent">
                {appt.booking_agent_name || appt.booking_agent || "—"}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Appt Create Date</div>
              <div className="text-slate-100" data-testid="appointment-created-at">
                {fmtDateTime(appt.created_at)}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Prospect Status</div>
              <div className="text-slate-100" data-testid="appointment-prospect-status">
                {prospectStatus}
              </div>
            </div>

            <div className="grid grid-cols-[160px_1fr] gap-3 text-sm">
              <div className="text-slate-200/70">Appointment Status</div>
              <div className="text-slate-100" data-testid="appointment-status">
                {appt?.summary_payload?.customer_status || appt?.status || "—"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
